"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.MechanicalNotAPartRule = void 0;
exports.resolveMechanicalNotAPartRule = resolveMechanicalNotAPartRule;
exports.isMechanicalNotAPart = isMechanicalNotAPart;
var MechanicalNotAPartRule;
(function (MechanicalNotAPartRule) {
    MechanicalNotAPartRule["MountingHole"] = "mounting_hole";
    MechanicalNotAPartRule["Fiducial"] = "fiducial";
    MechanicalNotAPartRule["NetTie"] = "net_tie";
    MechanicalNotAPartRule["SolderJumper"] = "solder_jumper";
    MechanicalNotAPartRule["BarePad"] = "bare_pad";
})(MechanicalNotAPartRule || (exports.MechanicalNotAPartRule = MechanicalNotAPartRule = {}));
const FEATURE_WORD_PATTERNS = [
    { rule: MechanicalNotAPartRule.MountingHole, pattern: /(?:^|[^a-z0-9])mounting[\s_-]*hole(?!s)/i },
    { rule: MechanicalNotAPartRule.Fiducial, pattern: /(?:^|[^a-z0-9])fiducial/i },
    { rule: MechanicalNotAPartRule.NetTie, pattern: /(?:^|[^a-z0-9])net[\s_-]*tie/i },
    { rule: MechanicalNotAPartRule.SolderJumper, pattern: /(?:^|[^a-z0-9])solder[\s_-]*jumper/i },
];
const PART_NAME_FIELD_PATTERNS = [
    ...FEATURE_WORD_PATTERNS,
    { rule: MechanicalNotAPartRule.SolderJumper, pattern: /(?:^|[^a-z0-9])jumper_\d+_bridged/i },
    { rule: MechanicalNotAPartRule.BarePad, pattern: /^(?:[^:]*:)?(?:PAD|PTH)_/i },
];
const IDENTIFIER_FIELD_PATTERNS = [
    ...PART_NAME_FIELD_PATTERNS,
    { rule: MechanicalNotAPartRule.MountingHole, pattern: /(?:^|[^a-z0-9])MH(?:[^a-z0-9]|$)/i },
];
const FOOTPRINT_PATTERNS = [
    { rule: MechanicalNotAPartRule.MountingHole, pattern: /^(?:[^:]*:)?mounting[\s_-]*hole/i },
    { rule: MechanicalNotAPartRule.Fiducial, pattern: /^(?:[^:]*:)?fiducial/i },
    { rule: MechanicalNotAPartRule.NetTie, pattern: /^(?:[^:]*:)?net[\s_-]*tie/i },
    { rule: MechanicalNotAPartRule.SolderJumper, pattern: /^(?:[^:]*:)?solder[\s_-]*jumper/i },
];
const REFERENCE_DESIGNATOR_PREFIX_RULES = new Map([
    ['MH', MechanicalNotAPartRule.MountingHole],
    ['FID', MechanicalNotAPartRule.Fiducial],
    ['NT', MechanicalNotAPartRule.NetTie],
]);
const SOLDER_JUMPER_REFERENCE_DESIGNATOR_PREFIX = 'JP';
const SOLDER_JUMPER_LIBRARY_PATTERN = /(?:^|[^a-z0-9])solder[\s_-]*jumper/i;
const IDENTIFIER_COLUMNS = new Set([
    'designitemid',
    'libref',
    'libreference',
    'libraryref',
    'libraryreference',
    'originalname',
]);
const PART_NAME_COLUMNS = new Set([
    'value',
    'comment',
    'partname',
    'componentname',
    'symbolname',
    'name',
]);
const LIBRARY_COLUMNS = new Set(['library', 'libraryname', 'sourcelibraryname', 'libid']);
const DESCRIPTION_COLUMNS = new Set(['description', 'desc', 'kidescription']);
function normalizeColumnName(column) {
    return column.toLowerCase().replace(/[^a-z0-9]/g, '');
}
function matchPatterns(texts, patterns) {
    for (const { rule, pattern } of patterns) {
        if (texts.some((text) => pattern.test(text))) {
            return rule;
        }
    }
    return null;
}
function collectFieldTexts(fields) {
    const identifiers = [];
    const partNames = [];
    const libraries = [];
    const descriptions = [];
    const footprints = [];
    const value = fields.value?.trim() ?? '';
    if (value !== '') {
        partNames.push(value);
    }
    for (const [column, cell] of Object.entries(fields.metadata)) {
        if (typeof cell !== 'string' || cell.trim() === '') {
            continue;
        }
        const text = cell.trim();
        const normalizedColumn = normalizeColumnName(column);
        if (IDENTIFIER_COLUMNS.has(normalizedColumn)) {
            identifiers.push(text);
        }
        else if (PART_NAME_COLUMNS.has(normalizedColumn)) {
            partNames.push(text);
        }
        else if (LIBRARY_COLUMNS.has(normalizedColumn)) {
            libraries.push(text);
        }
        else if (DESCRIPTION_COLUMNS.has(normalizedColumn)) {
            descriptions.push(text);
        }
        else if (normalizedColumn.includes('footprint')) {
            footprints.push(text);
        }
    }
    return { identifiers, partNames, libraries, descriptions, footprints };
}
function matchReferenceDesignator(referenceDesignator, libraryTexts) {
    const prefix = /^[a-z]+/i.exec(referenceDesignator?.trim() ?? '')?.[0]?.toUpperCase();
    if (prefix === undefined) {
        return null;
    }
    if (prefix === SOLDER_JUMPER_REFERENCE_DESIGNATOR_PREFIX) {
        return libraryTexts.some((text) => SOLDER_JUMPER_LIBRARY_PATTERN.test(text)) ?
            MechanicalNotAPartRule.SolderJumper
            : null;
    }
    return REFERENCE_DESIGNATOR_PREFIX_RULES.get(prefix) ?? null;
}
function isRealPartNumber(partNumber) {
    const trimmed = partNumber.trim();
    if (trimmed === '' || /^(?:https?:\/\/|www\.)/i.test(trimmed)) {
        return false;
    }
    return matchPatterns([trimmed], IDENTIFIER_FIELD_PATTERNS) === null;
}
function resolveMechanicalNotAPartRule(fields, partNumbers) {
    const { identifiers, partNames, libraries, descriptions, footprints } = collectFieldTexts(fields);
    const rule = matchPatterns(identifiers, IDENTIFIER_FIELD_PATTERNS) ??
        matchPatterns(partNames, PART_NAME_FIELD_PATTERNS) ??
        matchPatterns(descriptions, FEATURE_WORD_PATTERNS) ??
        matchPatterns(footprints, FOOTPRINT_PATTERNS) ??
        matchReferenceDesignator(fields.referenceDesignator, [...libraries, ...identifiers, ...partNames]);
    if (rule === null || partNumbers().some(isRealPartNumber)) {
        return null;
    }
    return rule;
}
function isMechanicalNotAPart(fields, partNumbers) {
    return resolveMechanicalNotAPartRule(fields, partNumbers) !== null;
}
//# sourceMappingURL=mechanical-not-a-part.js.map